/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ButtonsModal from '../../modal/ButtonsModal'
import { useDispatch, useSelector } from 'react-redux'
import { ModalPaymentWrapper, PaymentSectionTitle } from './index.styled'
import { selectContainsAnyRole, selectFormattedCurrency, selectHasAnyGroup } from '../../../../redux/selector/system'
import { selectDriverPaymentsEnabled } from '../../../../redux/selector/payment'
import ModalChangeCalculator from '../../changeCalculator'
import { PaymentCost, PaymentTitle, PaymentTitleWrapper } from '../index.styled'
import ModalTerminalButtons from '../ModalTerminalButtons'
import Currency from '../../currency'
import { createCartPaymentInvoiceConfigsThunk, getCartPaymentInvoiceConfigsThunk, loadCartPaymentDataThunk } from '../../../../redux/thunk/cartPayment'
import { getAdyenConnectedAccountThunk } from '../../../../redux/thunk/payment'
import ModalTerminalPaymentProcess from '../ModalTerminalPaymentProcess'
import { setCartPaymentGiftCoupons, setCartPaymentSelectedTerminal } from '../../../../redux/action/cartPayment'
import InvoiceModals from '../InvoiceModals'
import { PaymentHintButtonText, QrCodeButtonText, TerminalButtonText } from './buttonTexts'
import ModalQrCodePayment from '../ModalQrCodePayment'
import ActivateCouponModal from '../Coupon/ActivateCouponModal'
import { calculateGiftCouponsBalance } from '../../../CashDesk/helper'
import { setGiftCoupon } from '../../../../redux/action/coupon'
import { capitalize, roundPrice } from '../../../../helper'
import { getGiftCouponsSum } from '../../../../helper/getGiftCouponsPayment'
import { getPaymentBusinessScope } from './helper'
import { selectDisabledTerminalTips } from '../../../../redux/selector/cartPayment'
import { isDisabledTerminalTips } from '../../../../redux/selector/cartPayment/helper'
import TextInputWithKeyboard from './TextInputWithKeyboard'
import { filterSearchString } from '../../../../helper/filterSearchString'
import { setCustomer } from '../../../../redux/action/customer'
import { isLocalStorageMobileAppMode } from '../../../../helper/mode'
import { setShowPaymentMethodModal } from '../../../../redux/action/all-order'
import ModalChangeTipsCalculator from '../../changeTipsCalculator'
import Tap2PayModals from '../Tap2PayModals'
import RoomChargeModal from '../../../RoomCharge/RoomChargeModal'
import RoomChargeButtonText from '../../../RoomCharge/RoomChargeButtonText'
import { ROOM_CHARGE_ORDER_TYPES, ROOM_CHARGE_PAYMENT_METHOD } from '../../../RoomCharge/constants'
import { selectIsPmsIntegrationEnabled } from '../../../../redux/selector/pmsIntegration'
import { getPmsIntegrationThunk } from '../../../../redux/thunk/pmsIntegration'
import { selectRoomChargeOrderId } from '../../../../redux/selector/roomCharge'
import { fetchPrintSettingsThunk } from '../../../../redux/thunk/printReceipt'
import ButtonIcon from '../../../../img/svg/button'
import { useOrderInvoice } from '../../table/InvoicesTable/hooks/useOrderInvoice'

const EVENT_Z_INDEX = 4

const STRIPE_MIN_ORDER_AMOUNT = 0.5

const DRIVER_PAYMENT_METHODS = ['CASH', 'EC_AT_DOORSTEP', 'CREDIT_CARD_AT_DOORSTEP']

const ModalPayment = React.memo(({
	id = 'cart-payment', isOpen, closeModal, openModal,
	remark, totalSum, module, orderType, handlePayment, resetCart,
	changeCalculatorIsNeeded, isDeferredPayment = false, updatePaymentMode = false, isPreorder = false,
	isMoneyExchange = false, updateOrder = false, platformType = '', customerOrder, orderId = null,
}) => {
	const { t } = useTranslation()
	const customer = useSelector(store => store.customerStore.customer)
	const { invoice: activeOrderInvoice, isLoading: isOrderInvoiceLoading } = useOrderInvoice(orderId, !!(isOpen && updatePaymentMode && orderId))
	const [remarkText, setRemarkText] = useState('')
	const [customerName, setCustomerName] = useState('')
	const [focusedInputName, setFocusedInputName] = useState(null)
	const [paymentMethod, setPaymentMethod] = useState('')
	const [modalChangeCalculatorIsOpen, setModalChangeCalculatorIsOpen] = useState(false)
	const [modalChangeTipsCalculatorIsOpen, setModalChangeTipsCalculatorIsOpen] = useState(false)
	const [modalTerminalButtonsIsOpen, setModalTerminalButtonsIsOpen] = useState(false)
	const [modalTerminalPaymentProcessIsOpen, setModalTerminalPaymentProcessIsOpen] = useState(false)
	const [ecTerminalTips, setEcTerminalTips] = useState(0)
	const [invoiceTips, setInvoiceTips] = useState(0)
	const [modalInvoiceConfigsIsOpen, setModalInvoiceConfigsIsOpen] = useState(false)
	const [modalInvoiceCustomerDataIsOpen, setModalInvoiceCustomerDataIsOpen] = useState(false)
	const [modalQrCodePaymentIsOpen, setModalQrCodePaymentIsOpen] = useState(false)
	const [modalActivateCouponIsOpen, setModalActivateCouponIsOpen] = useState(false)
	const [showTap2PayAvailabilityCheckerModal, setShowTap2PayAvailabilityCheckerModal] = useState(false)
	const [showChangePaymentAmountModal, setShowChangePaymentAmountModal] = useState(false)

	const hasAdminRole = useSelector(selectHasAnyGroup(['OWNER', 'SUPERADMIN', 'ADMIN']))
	const hasRoleSettingsReadOrWrite = useSelector(selectContainsAnyRole(['PROGRAM_SETTINGS_READ', 'PROGRAM_SETTINGS_WRITE']))

	const payments = useSelector(store => store.cartPayment.payments)
	const terminals = useSelector(store => store.cartPayment.terminals)
	const terminalsLength = useSelector(store => store.cartPayment.terminalsLength)
	const waiterAccessToken = useSelector(state => state.modalLogin.accessToken)
	const formattedStripeLimitSum = useSelector(selectFormattedCurrency(STRIPE_MIN_ORDER_AMOUNT))
	const disabledTerminalTips = useSelector(selectDisabledTerminalTips(orderType))
	const hasModalLogin = !!waiterAccessToken
	const dispatch = useDispatch()
	const driverPaymentsEnabled = useSelector(selectDriverPaymentsEnabled)

	const isDelivery = orderType === 'DELIVERY'
	const isRestaurant = orderType === 'RESTAURANT'

	const [showRoomChargeModal, setShowRoomChargeModal] = useState(false)
	const [roomChargeTotal, setRoomChargeTotal] = useState(0)
	const isRoomChargeEnabled = useSelector(selectIsPmsIntegrationEnabled)
	const roomChargeOrderId = useSelector(selectRoomChargeOrderId)
	const isRoomChargeOrderType = ROOM_CHARGE_ORDER_TYPES.includes(orderType)
	const showRoomChargeButton = isRoomChargeEnabled && isRoomChargeOrderType && !isMoneyExchange && !isDeferredPayment && !updatePaymentMode

	const isNeededDeferredPayment = (orderType === 'PICKUP' || (isDelivery && driverPaymentsEnabled)) && !updatePaymentMode && !isDeferredPayment
	const showDriverPaymentSection = isNeededDeferredPayment && isDelivery && !isMoneyExchange
	const isExistInvoicePaymentConfig = !!useSelector(state => state.cartPayment.invoiceConfigs)
	const disableInvoicePayment = !hasAdminRole && !isExistInvoicePaymentConfig

	const giftCoupon = useSelector(state => state.coupon?.giftCoupon)
	const giftCoupons = useSelector(state => state.cartPayment.giftCoupons)

	const giftCouponSum = giftCoupons?.length ? getGiftCouponsSum(giftCoupons) : 0
	const totalPriceWithGiftCoupon = totalSum >= 0 ? roundPrice(totalSum - giftCouponSum) : null

	const isStripePaymentBelowLimit = totalPriceWithGiftCoupon < STRIPE_MIN_ORDER_AMOUNT
	const paymentBusinessScope = getPaymentBusinessScope(orderType)

	const isMobileApp = isLocalStorageMobileAppMode()
	const tap2PayTips = useSelector(store => store.tap2Pay.tap2PayTips)
	const isCartPaymentLoading = useSelector(store => store.cartPayment.isDataLoading)

	useEffect(() => {
		if (isOpen) {
			dispatch(loadCartPaymentDataThunk(paymentBusinessScope, hasModalLogin))
			dispatch(setCartPaymentGiftCoupons([]))
			if (isDelivery) {
				dispatch(getAdyenConnectedAccountThunk())
			}
		}
	}, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isOpen && hasRoleSettingsReadOrWrite) {
			dispatch(getCartPaymentInvoiceConfigsThunk())
		}
	}, [isOpen, hasRoleSettingsReadOrWrite]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isOpen && customer && customer.name) {
			setCustomerName(customer.name)
		}
	}, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isOpen && module === 'CASH_DESK') {
			dispatch(setCustomer(null))
			setCustomerName('')
		}
	}, [dispatch, module, isOpen])

	useEffect(() => {
		if (remark) setRemarkText(remark)
	}, [remark])

	useEffect(() => {
		if (isOpen) {
			const isExistGiftCoupon = giftCoupons?.findIndex(item => item?.id === giftCoupon?.id)
			if (giftCoupon && isExistGiftCoupon === -1) {
				let allGiftCoupons = [...giftCoupons, { ...giftCoupon, cartBalance: 0 }]
				const giftCouponsWithBalance = calculateGiftCouponsBalance(allGiftCoupons, totalSum)
				dispatch(setCartPaymentGiftCoupons(giftCouponsWithBalance))
				dispatch(setGiftCoupon(null))
			}
		}
	}, [giftCoupon, isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isOpen && totalPriceWithGiftCoupon <= 0 && giftCoupons?.length) {
			handlePayment({ paymentMethod: 'GIFT_COUPON', giftCoupons })
			handleClose()
		}
	}, [giftCoupons, isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

	const resetRemark = useCallback(() => {
		setRemarkText('')
	}, [])

	const resetCustomerName = useCallback(() => {
		setCustomerName('')
	}, [])

	const resetInputValues = useCallback(() => {
		resetCustomerName()
		resetRemark()
	}, [resetCustomerName, resetRemark])

	const handleEcTerminalPaymentClick = useCallback(() => {
		if (terminalsLength > 1) {
			setModalTerminalButtonsIsOpen(true)
		} else {
			const disabledTips = isDisabledTerminalTips(terminals[0], orderType)
			dispatch(setCartPaymentSelectedTerminal(terminals[0]))
			if (isMoneyExchange || disabledTips) {
				setModalTerminalPaymentProcessIsOpen(true)
			} else {
				setModalChangeCalculatorIsOpen(true)
			}
		}
	}, [dispatch, terminals, terminalsLength, isMoneyExchange, orderType])

	const handleCashPaymentClick = useCallback(async (remark) => {
		if (changeCalculatorIsNeeded) {
			if (!isDelivery) {
				setModalChangeTipsCalculatorIsOpen(true)
			} else {
				setModalChangeCalculatorIsOpen(true)
			}
		} else {
			await handlePayment({ paymentMethod: 'CASH', remark, giftCoupons })
			resetInputValues()
			dispatch(setCartPaymentGiftCoupons([]))
		}
	}, [dispatch, changeCalculatorIsNeeded, handlePayment, resetInputValues, giftCoupons, isDelivery])

	const openModalInvoiceCustomerData = useCallback(() => {
		if ((isDeferredPayment || updatePaymentMode) && customerOrder) {
			dispatch(setCustomer({ ...customerOrder }))
		}
		setModalInvoiceCustomerDataIsOpen(true)
	}, [customerOrder, dispatch, isDeferredPayment, updatePaymentMode])

	const handleInvoicePaymentClick = useCallback(() => {
		if (!isExistInvoicePaymentConfig) {
			setModalInvoiceConfigsIsOpen(true)
		} else if (updatePaymentMode) {
			openModalInvoiceCustomerData()
		} else {
			setModalChangeCalculatorIsOpen(true)
		}
		resetCustomerName()
	}, [isExistInvoicePaymentConfig, updatePaymentMode, openModalInvoiceCustomerData, resetCustomerName])

	const couponPayment = useCallback(() => {
		setPaymentMethod('GIFT_COUPON')
		setModalActivateCouponIsOpen(true)
	}, [])

	const handleTap2PayPaymentClick = useCallback(() => {
		setShowTap2PayAvailabilityCheckerModal(true)
	}, [setShowTap2PayAvailabilityCheckerModal])

	useEffect(() => {
		if (isOpen && isRoomChargeOrderType && !isMoneyExchange && !updatePaymentMode) dispatch(getPmsIntegrationThunk())
	}, [isOpen, isRoomChargeOrderType, isMoneyExchange, updatePaymentMode, dispatch])

	const closeRoomChargeModal = useCallback(() => {
		setShowRoomChargeModal(false)
		setRoomChargeTotal(0)
	}, [])

	const finishRoomCharge = useCallback(() => {
		dispatch(fetchPrintSettingsThunk(roomChargeOrderId, { module, platformType, businessScope: orderType }))
		resetCart()
	}, [dispatch, roomChargeOrderId, module, platformType, orderType, resetCart])

	const handleRoomChargeClick = useCallback(async () => {
		setPaymentMethod(ROOM_CHARGE_PAYMENT_METHOD)
		setRoomChargeTotal(totalPriceWithGiftCoupon)
		await handlePayment({ paymentMethod: ROOM_CHARGE_PAYMENT_METHOD, remark: remarkText.trim(), giftCoupons })
		setShowRoomChargeModal(true)
		closeModal()
	}, [handlePayment, remarkText, giftCoupons, closeModal, totalPriceWithGiftCoupon])

	const handlePaymentClick = useCallback(async (paymentMethod) => {
		setPaymentMethod(paymentMethod)
		const remarkNotEmpty = remarkText.trim() === '' ? null : remarkText
		const isECTerminal = paymentMethod === 'EC_TERMINAL'
		const isCash = paymentMethod === 'CASH'
		const isInvoice = paymentMethod === 'INVOICE'
		const isQrCode = paymentMethod === 'QR_CODE'
		const isTap2Pay = paymentMethod === 'TAP_TO_PAY'

		if (isECTerminal) {
			handleEcTerminalPaymentClick()
		} else if (isCash) {
			await handleCashPaymentClick(remarkNotEmpty)
		} else if (isInvoice) {
			handleInvoicePaymentClick()
		} else if (isQrCode) {
			setModalQrCodePaymentIsOpen(true)
		} else if (isTap2Pay) {
			handleTap2PayPaymentClick()
		} else {
			await handlePayment({ paymentMethod, remark: remarkNotEmpty, giftCoupons })
			resetInputValues()
			dispatch(setCartPaymentGiftCoupons([]))
		}
		closeModal(!isECTerminal && !isTap2Pay && (!isQrCode && !isInvoice && !isDeferredPayment))
	}, [dispatch, closeModal, handlePayment, remarkText, setPaymentMethod, handleCashPaymentClick, handleEcTerminalPaymentClick, handleInvoicePaymentClick, resetInputValues, giftCoupons, isDeferredPayment, handleTap2PayPaymentClick])

	const handleDeferredPaymentClick = useCallback(async (paymentMethod) => {
		const remarkNotEmpty = remarkText.trim() === '' ? null : remarkText
		await handlePayment({ paymentMethod, remark: remarkNotEmpty, deferred: true })
		resetInputValues()
		closeModal(true)
	}, [closeModal, handlePayment, remarkText, resetInputValues])


	const closeModalTerminalPaymentProcess = useCallback(() => {
		dispatch(setCartPaymentSelectedTerminal(null))
		dispatch(setCartPaymentGiftCoupons([]))
		setModalTerminalPaymentProcessIsOpen(false)
	}, [dispatch])

	const handlePaymentTerminal = useCallback(async (paymentId, tipsFromTerminal) => {
		const remarkNotEmpty = remarkText.trim() === '' ? null : remarkText
		await handlePayment({ paymentId, paymentMethod: 'EC_TERMINAL', tips: ecTerminalTips + tipsFromTerminal, remark: remarkNotEmpty, giftCoupons })
		resetInputValues()
	}, [handlePayment, ecTerminalTips, remarkText, resetInputValues, giftCoupons])

	const handlePaymentInvoice = useCallback((customer) => {
		setModalInvoiceCustomerDataIsOpen(false)
		const remarkNotEmpty = remarkText.trim() === '' ? null : remarkText
		handlePayment({ paymentMethod: 'INVOICE', remark: remarkNotEmpty, customer, tips: invoiceTips, giftCoupons })
		setInvoiceTips(0)
		resetInputValues()
		dispatch(setCartPaymentGiftCoupons([]))
	}, [dispatch, handlePayment, remarkText, resetInputValues, giftCoupons, invoiceTips])

	const disablePaymentButton = useCallback((paymentMethod) => {
		switch (paymentMethod) {
			case 'EC_TERMINAL':
				return !terminalsLength || isStripePaymentBelowLimit
			case 'INVOICE':
				return disableInvoicePayment
			case 'TAP_TO_PAY':
			case 'QR_CODE':
				return isStripePaymentBelowLimit
			default:
				return false
		}
	}, [terminalsLength, isStripePaymentBelowLimit, disableInvoicePayment])

	const showPaymentButton = useCallback((payment) => {
		switch (payment.paymentMethod) {
			case 'INVOICE':
				return !isMoneyExchange && !activeOrderInvoice && !isOrderInvoiceLoading
			case 'QR_CODE':
				return !isPreorder && !isDelivery && !updatePaymentMode && !isMoneyExchange && !updateOrder
			case 'CASH':
				return !isMoneyExchange
			case 'TAP_TO_PAY':
				return isMobileApp && payment.enabled && (isRestaurant || !isDelivery)
			case 'EC_AT_DOORSTEP':
				return !showDriverPaymentSection
			default:
				return true
		}
	}, [isPreorder, isDelivery, updatePaymentMode, isMoneyExchange, updateOrder, isMobileApp, isRestaurant, showDriverPaymentSection, activeOrderInvoice, isOrderInvoiceLoading])

	const paymentButtonMessage = useCallback((paymentMethod) => {
		const isEcTerminal = paymentMethod === 'EC_TERMINAL'
		const isInvoice = paymentMethod === 'INVOICE'
		const isTap2Pay = paymentMethod === 'TAP_TO_PAY'
		const isQrCode = paymentMethod === 'QR_CODE'

		if (isInvoice && disableInvoicePayment) {
			return { text: t('Modal.payment.invoice_disable_message') }
		} else if (!isDelivery && (isEcTerminal || isTap2Pay) && isStripePaymentBelowLimit) {
			return { text: t('Modal.payment.terminal_disable_message', { sum: formattedStripeLimitSum }) }
		} else if (!isDelivery && isQrCode && isStripePaymentBelowLimit) {
			return { text: t('Modal.payment.qrcode_disable_message', { sum: formattedStripeLimitSum }) }
		} else {
			return null
		}
	}, [isDelivery, disableInvoicePayment, isStripePaymentBelowLimit, t, formattedStripeLimitSum])

	const paymentButtonText = useCallback((paymentMethod, isDriver = false) => {
		const isQrCode = paymentMethod === 'QR_CODE'
		const isEcCard = paymentMethod === 'EC_AT_DOORSTEP'
		const isEcTerminal = paymentMethod === 'EC_TERMINAL'
		const isFahrerApp = paymentMethod === 'CREDIT_CARD_AT_DOORSTEP'

		if (isDriver && isFahrerApp) {
			return <PaymentHintButtonText text={t('order.payment_type.fahrer_app')} hint={t('order.payment_type.fahrer_app_hint')} />
		} else if (isDriver && paymentMethod === 'CASH') {
			return <PaymentHintButtonText text={t('Payments.payments.CASH')} hint={t('order.payment_type.at_driver')} />
		} else if (isQrCode) {
			return <QrCodeButtonText text={t(`Payments.payments.${paymentMethod}`)} />
		} else if (isDelivery && (isEcCard || isEcTerminal)) {
			return <TerminalButtonText paymentMethod={isEcCard ? t('Payments.payments.portable_terminal') : t('Payments.payments.stationary_terminal')}
																 infoPaymentMethod={t(`Payments.payments.${paymentMethod}`)} />
		} else {
			return t(`Payments.payments.${paymentMethod}`)
		}
	}, [isDelivery, t])

	const dynamicPaymentButtons = useMemo(() => {
		const staticGiftCouponPayment = { icon: 'GIFT_COUPON', text: t('order.payment_type.coupon'), onClick: () => couponPayment(), show: isDeferredPayment && !isMoneyExchange, disabled: false, testId: 'paymentButton' }
		const staticOnlinePayment = { icon: 'ONLINE', text: t('order.payment_type.online'), onClick: () => handlePaymentClick('ONLINE'), show: !isDeferredPayment && !isMoneyExchange, disabled: false, testId: 'paymentButton' }
		const deferredPaymentButton = { icon: 'payment', text: t('order.payment_type.deferred_payment'), onClick: () => handleDeferredPaymentClick('CASH'), show: isNeededDeferredPayment && !isDelivery && !isMoneyExchange, disabled: false, testId: 'paymentButton' }
		const paymentsWithoutAdditionalMethods = payments.filter(payment => !payment.paymentName)

		const dynamicPayments = paymentsWithoutAdditionalMethods.map(payment => {
			return ({
				icon: payment?.paymentMethod,
				text: paymentButtonText(payment.paymentMethod),
				onClick: () => handlePaymentClick(payment.paymentMethod),
				show: showPaymentButton(payment),
				disabled: payment?.disabledForPay || disablePaymentButton(payment.paymentMethod),
				testId: 'paymentButton',
				message: paymentButtonMessage(payment.paymentMethod)
			})
		})
		dynamicPayments.splice(1, 0, staticOnlinePayment)
		const roomChargeButton = {
			icon: 'ROOM_CHARGE',
			text: <RoomChargeButtonText text={t('Payments.payments.ROOM_CHARGE')} hint={t('RoomCharge.methodHint')} />,
			onClick: () => handleRoomChargeClick(),
			show: showRoomChargeButton,
			disabled: false,
			testId: 'paymentButton',
		}
		return [staticGiftCouponPayment, ...dynamicPayments, roomChargeButton, deferredPaymentButton]
	}, [payments, handlePaymentClick, isDeferredPayment, isNeededDeferredPayment, isDelivery, handleDeferredPaymentClick, t, disablePaymentButton, showPaymentButton, isMoneyExchange, paymentButtonMessage, couponPayment, paymentButtonText, showRoomChargeButton, handleRoomChargeClick])

	const driverPaymentButtons = useMemo(() => DRIVER_PAYMENT_METHODS.map(method => ({
		icon: method,
		text: paymentButtonText(method, true),
		onClick: () => handleDeferredPaymentClick(method),
		show: true,
		disabled: false,
		testId: 'paymentButton'
	})), [paymentButtonText, handleDeferredPaymentClick])

	const sectionedPaymentButtons = useMemo(() => [
		...dynamicPaymentButtons.map(button => ({ ...button, section: 'now' })),
		...driverPaymentButtons.map(button => ({ ...button, section: 'driver' }))
	], [dynamicPaymentButtons, driverPaymentButtons])

	const paymentSections = useMemo(() => [
		{
			key: 'now', testId: 'now-payment-section',
			title: <PaymentSectionTitle className="now"><ButtonIcon icon="cashdesk" />{t('order.payment_type.now_section')}</PaymentSectionTitle>
		},
		{
			key: 'driver', testId: 'driver-payment-section',
			title: <PaymentSectionTitle className="driver"><ButtonIcon icon="big-car" />{t('order.payment_type.driver_section')}</PaymentSectionTitle>
		}
	], [t])

	const onChangeRemark = useCallback((value) => {
		setRemarkText(value)
	}, [])

	const onChangeCustomerName = useCallback((value) => {
		const replaceForbiddenSymbol = filterSearchString(value)
		setCustomerName(capitalize(replaceForbiddenSymbol))
	}, [])

	const onInputFocus = useCallback((name) => {
		setFocusedInputName(name)
	}, [])

	const onBlurRemark = useCallback(() => {
		setFocusedInputName(null)
	}, [])

	const onBlurCustomerName = useCallback(() => {
		const customerNameTrimmed = customerName.trim() === '' ? null : customerName
		if (!customer && !customerNameTrimmed) {
			dispatch(setCustomer(null))
		} else if (customerNameTrimmed) {
			dispatch(setCustomer({
				...customer,
				name: customerNameTrimmed
			}))
		}
		setFocusedInputName(null)
	}, [dispatch, customer, customerName])

	const handleClose = useCallback(() => {
		dispatch(setCartPaymentGiftCoupons([]))
		resetInputValues()
		closeModal()
	}, [dispatch, closeModal, resetInputValues])

	const closeChangeModal = useCallback(() => {
		setModalChangeCalculatorIsOpen(false)
		dispatch(setCartPaymentSelectedTerminal(null))
		if (paymentMethod === 'EC_TERMINAL' && terminalsLength > 1) {
			setModalTerminalButtonsIsOpen(true)
		} else {
			openModal()
		}
	}, [openModal, terminalsLength, paymentMethod, dispatch])

	const closeChangeTipsModal = useCallback(() => {
		setModalChangeTipsCalculatorIsOpen(false)
		openModal()
	}, [openModal])

	const handleModalChangePayment = useCallback(async ({ differenceSum }) => {
		if (paymentMethod === 'EC_TERMINAL') {
			setEcTerminalTips(differenceSum)
			setModalTerminalPaymentProcessIsOpen(true)
			setModalChangeCalculatorIsOpen(false)
		} else if (paymentMethod === 'INVOICE') {
			setInvoiceTips(differenceSum)
			setModalChangeCalculatorIsOpen(false)
			openModalInvoiceCustomerData()
		} else {
			const remarkNotEmpty = remarkText.trim() === '' ? null : remarkText
			await handlePayment({ paymentMethod: 'CASH', remark: remarkNotEmpty, giftCoupons })
			setModalChangeCalculatorIsOpen(false)
			resetInputValues()
			dispatch(setCartPaymentGiftCoupons([]))
		}
	}, [dispatch, paymentMethod, remarkText, handlePayment, resetInputValues, giftCoupons, openModalInvoiceCustomerData])

	const handleModalChangeTipsPayment = useCallback(async ({ tips }) => {
		const remarkNotEmpty = remarkText.trim() === '' ? null : remarkText
		await handlePayment({ paymentMethod: 'CASH', remark: remarkNotEmpty, tips, giftCoupons })
		setModalChangeTipsCalculatorIsOpen(false)
		resetInputValues()
		dispatch(setCartPaymentGiftCoupons([]))
	}, [dispatch, remarkText, handlePayment, resetInputValues, giftCoupons])

	const closeTerminalButtonsModal = useCallback(() => {
		setModalTerminalButtonsIsOpen(false)
		openModal()
	}, [openModal])

	const reOpenModalChangeCalculator = useCallback(() => {
		setModalTerminalPaymentProcessIsOpen(false)
		if (isMoneyExchange || disabledTerminalTips) {
			closeChangeModal()
		} else {
			setModalChangeCalculatorIsOpen(true)
		}
	}, [closeChangeModal, isMoneyExchange, disabledTerminalTips])

	const handleTerminalSelected = useCallback((item) => {
		setModalTerminalButtonsIsOpen(false)
		const disabledTips = isDisabledTerminalTips(item, orderType)
		if (isMoneyExchange || disabledTips) {
			setModalTerminalPaymentProcessIsOpen(true)
		} else {
			setModalChangeCalculatorIsOpen(true)
		}
	}, [isMoneyExchange, orderType])

	const submitInvoiceConfig = useCallback(async (data) => {
		await dispatch(createCartPaymentInvoiceConfigsThunk(data))
		setModalInvoiceConfigsIsOpen(false)
		if (updatePaymentMode) {
			openModalInvoiceCustomerData()
		} else {
			setModalChangeCalculatorIsOpen(true)
		}
	}, [dispatch, updatePaymentMode, openModalInvoiceCustomerData])

	const closeInvoiceConfigsModal = useCallback(() => {
		setModalInvoiceConfigsIsOpen(false)
		openModal()
	}, [openModal])

	const closeInvoiceCustomerDataModal = useCallback(() => {
		setModalInvoiceCustomerDataIsOpen(false)
		openModal()
	}, [openModal])

	const closeModalQrCodePayment = useCallback(() => {
		dispatch(setCartPaymentGiftCoupons([]))
		setModalQrCodePaymentIsOpen(false)
	}, [dispatch])

	const closeModalActivateCoupon = useCallback(() => {
		setModalActivateCouponIsOpen(false)
	}, [])

	const handlePaymentQrCode = useCallback(async (paymentId, paymentLink) => {
		const remarkNotEmpty = remarkText.trim() === '' ? null : remarkText
		await handlePayment({ paymentId, paymentLink, paymentMethod: 'QR_CODE', remark: remarkNotEmpty, giftCoupons })
		resetInputValues()
	}, [handlePayment, remarkText, resetInputValues, giftCoupons])

	const handleTap2PayPayment = useCallback((paymentId) => {
		const remarkNotEmpty = remarkText.trim() === '' ? null : remarkText
		handlePayment({ paymentId, paymentMethod: 'TAP_TO_PAY', tips: tap2PayTips, remark: remarkNotEmpty, giftCoupons })
		dispatch(setCartPaymentGiftCoupons([]))
	}, [dispatch, tap2PayTips, giftCoupons, handlePayment, remarkText])

	const closeChangePaymentAmountModal = useCallback(() => {
		setShowChangePaymentAmountModal(false)
		dispatch(setShowPaymentMethodModal(true))
	}, [dispatch])

	return (
		<ModalPaymentWrapper>
			<ButtonsModal id={id}
										isOpen={isOpen}
										close={handleClose}
										size={showDriverPaymentSection ? 'medium' : 'small'}
										title={t('Modal.payment.title')}
										zIndex={EVENT_Z_INDEX}
										buttons={showDriverPaymentSection ? sectionedPaymentButtons : dynamicPaymentButtons}
										sections={showDriverPaymentSection ? paymentSections : null}
										mobileIndentIsNeeded={true}
										isLoading={isCartPaymentLoading}>
				{giftCoupons?.length > 0 ?
					<PaymentTitleWrapper className="with-gift-coupon">
						<PaymentTitle>{t('Cart.leftToPay')}: </PaymentTitle>
						<PaymentCost><Currency>{totalPriceWithGiftCoupon}</Currency></PaymentCost>
						<PaymentTitle>({t('Cart.total')}: <Currency>{totalSum}</Currency>)</PaymentTitle>
					</PaymentTitleWrapper> :
					<PaymentTitleWrapper>
						<PaymentTitle>{t('Cart.titlePrice')}: </PaymentTitle>
						<PaymentCost><Currency>{totalSum}</Currency></PaymentCost>
					</PaymentTitleWrapper>}
				{orderType === 'PICKUP' && !updatePaymentMode && !isMoneyExchange &&
					<TextInputWithKeyboard name="customerName"
																 value={customerName}
																 onChange={onChangeCustomerName}
																 onBlur={onBlurCustomerName}
																 placeholder={t('AddOrder.Client.form.Name')}
																 disabled={focusedInputName && focusedInputName !== 'customerName'}
																 onFocus={onInputFocus} />}
				{!isDeferredPayment && !isMoneyExchange &&
					<TextInputWithKeyboard name="remark"
																 value={remarkText}
																 onChange={onChangeRemark}
																 onBlur={onBlurRemark}
																 placeholder={t('Modal.notice')}
																 disabled={focusedInputName && focusedInputName !== 'remark'}
																 onFocus={onInputFocus} />}
			</ButtonsModal>

			<ModalTerminalButtons isOpen={modalTerminalButtonsIsOpen}
														close={closeTerminalButtonsModal}
														totalPrice={totalPriceWithGiftCoupon}
														onTerminalSelected={handleTerminalSelected} />
			<ModalChangeCalculator isOpen={modalChangeCalculatorIsOpen}
														 close={closeChangeModal}
														 totalPrice={totalPriceWithGiftCoupon}
														 isEcTerminal={paymentMethod === 'EC_TERMINAL'}
														 isInvoice={paymentMethod === 'INVOICE'}
														 handlePayment={handleModalChangePayment} />
			<ModalChangeTipsCalculator isOpen={modalChangeTipsCalculatorIsOpen}
																 close={closeChangeTipsModal}
																 totalPrice={totalPriceWithGiftCoupon}
																 handlePayment={handleModalChangeTipsPayment} />
			<ModalTerminalPaymentProcess isOpen={modalTerminalPaymentProcessIsOpen}
																	 totalPrice={totalPriceWithGiftCoupon + ecTerminalTips}
																	 navigateBack={reOpenModalChangeCalculator}
																	 close={closeModalTerminalPaymentProcess}
																	 handlePayment={handlePaymentTerminal}
																	 isMoneyExchange={isMoneyExchange}
																	 resetCart={resetCart}
																	 ecTerminalTips={ecTerminalTips}
																	 printReceipt={true}
																	 module={module}
																	 orderType={orderType}
																	 platformType={platformType} />
			<InvoiceModals invoiceConfigsModalIsOpen={modalInvoiceConfigsIsOpen}
										 closeInvoiceConfigsModal={closeInvoiceConfigsModal}
										 submitInvoiceConfig={submitInvoiceConfig}
										 invoiceCustomerDataModalIsOpen={modalInvoiceCustomerDataIsOpen}
										 closeInvoiceCustomerDataModal={closeInvoiceCustomerDataModal}
										 onSubmit={handlePaymentInvoice}
										 captureCustomerData={isDelivery} />
			<ModalQrCodePayment isOpen={modalQrCodePaymentIsOpen}
													totalPrice={totalPriceWithGiftCoupon}
													close={closeModalQrCodePayment}
													openPaymentModal={openModal}
													handlePayment={handlePaymentQrCode}
													resetCart={resetCart}
													printReceipt={!updatePaymentMode || isDeferredPayment}
													module={module}
													orderType={orderType}
													platformType={platformType} />
			<Tap2PayModals totalPrice={totalPriceWithGiftCoupon}
										 showTap2PayAvailabilityCheckerModal={showTap2PayAvailabilityCheckerModal}
										 setShowTap2PayAvailabilityCheckerModal={setShowTap2PayAvailabilityCheckerModal}
										 setShowChangePaymentAmountModal={setShowChangePaymentAmountModal}
										 showChangePaymentAmountModal={showChangePaymentAmountModal}
										 handlePayment={handleTap2PayPayment}
										 closeChangePaymentAmountModal={closeChangePaymentAmountModal}
										 resetCart={resetCart}
										 module={module}
										 orderType={orderType}
										 paymentSource={isRestaurant && !updatePaymentMode ? 'RESTAURANT' : 'CASH_REGISTER'}
										 printReceipt={true} />
			<ActivateCouponModal isOpen={modalActivateCouponIsOpen}
													 close={closeModalActivateCoupon}
													 isGiftCouponMode={true}
													 giftCouponsType="CART_PAYMENT" />
			<RoomChargeModal isOpen={showRoomChargeModal}
											 close={closeRoomChargeModal}
											 total={roomChargeTotal}
											 onCharged={finishRoomCharge} />

		</ModalPaymentWrapper>
	)
})

ModalPayment.displayName = 'ModalPayment'
export default ModalPayment
